import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { adminPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property } from "@/types"

interface Props {
  property: Property
  statuses: string[]
}

const statusLabel: Record<string, string> = {
  available: "Available",
  under_negotiation: "Under Negotiation",
  leased: "Leased",
}

export default function New({ property, statuses }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: adminPropertiesPath() },
    { title: property.name, href: `/admin/properties/${property.id}` },
    { title: "New Suite", href: `/admin/properties/${property.id}/suites/new` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Suite" />

      <div className="max-w-2xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">New Suite</h1>
          <p className="text-muted-foreground text-sm">{property.name}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Suite Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form
              method="post"
              action={`/admin/properties/${property.id}/suites`}
              encType="multipart/form-data"
              className="space-y-4"
            >
              {({ errors, processing }) => (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="suite_number">Suite Number</Label>
                      <Input id="suite_number" name="suite_number" placeholder="e.g. 101" />
                      <InputError messages={errors.suite_number} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="floor">Floor</Label>
                      <Input id="floor" name="floor" type="number" placeholder="e.g. 1" />
                      <InputError messages={errors.floor} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="sqft">Square Footage</Label>
                      <Input id="sqft" name="sqft" type="number" placeholder="e.g. 1200" />
                      <InputError messages={errors.sqft} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="monthly_rent">Monthly Rent ($)</Label>
                      <Input id="monthly_rent" name="monthly_rent" type="number" placeholder="e.g. 3500" />
                      <InputError messages={errors.monthly_rent} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="available_on">Available On</Label>
                      <Input id="available_on" name="available_on" type="date" />
                      <InputError messages={errors.available_on} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="status">Status</Label>
                      <Select name="status" defaultValue="available">
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((s) => (
                            <SelectItem key={s} value={s}>
                              {statusLabel[s] ?? s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InputError messages={errors.status} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="rate_per_sqft">Rate ($/SF/YR)</Label>
                      <Input id="rate_per_sqft" name="rate_per_sqft" type="number" step="0.01" placeholder="25.00" />
                      <InputError messages={errors.rate_per_sqft} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="lease_type">Lease Type</Label>
                      <Input id="lease_type" name="lease_type" defaultValue="NNN" />
                      <InputError messages={errors.lease_type} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="dimensions">Dimensions</Label>
                      <Input id="dimensions" name="dimensions" placeholder="46' x 89'" />
                      <InputError messages={errors.dimensions} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="map_marker">Site Map Marker #</Label>
                      <Input id="map_marker" name="map_marker" type="number" />
                      <InputError messages={errors.map_marker} />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="available_label">Marketing Headline</Label>
                    <Input id="available_label" name="available_label" placeholder="Suite 5 Available" />
                    <p className="text-muted-foreground text-xs">Shown in the dark callout band on the Marketing brochure.</p>
                    <InputError messages={errors.available_label} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="features">Bullet Features (one per line)</Label>
                    <Textarea id="features" name="features" rows={4} placeholder="Maximum visibility&#10;Corner suite which opens into a large space&#10;Includes showroom, storage, bathroom" />
                    <InputError messages={errors.features} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea id="description" name="description" placeholder="Suite details..." rows={3} />
                    <InputError messages={errors.description} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="photos">Photos</Label>
                    <Input id="photos" name="photos[]" type="file" multiple accept="image/*" />
                    <InputError messages={errors.photos} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" disabled={processing}>Add Suite</Button>
                    <Button variant="outline" type="button" onClick={() => history.back()}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
